import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type {
  ShiftTemplate,
  CreateShiftTemplateInput,
  UpdateShiftTemplateInput,
  DaySpecificTimes
} from '@/types/shift-templates';
import type { Json } from '@/integrations/supabase/types';

export function useShiftTemplates(patientId: string | undefined) {
  return useQuery({
    queryKey: ['shift-templates', patientId],
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from('shift_templates')
        .select('*')
        .eq('patient_id', patientId)
        .is('deleted_at', null)
        .order('name');

      if (error) throw error;

      // Cast the data to our type with proper handling of JSONB
      return (data || []).map(item => ({
        ...item,
        day_specific_times: item.day_specific_times as unknown as DaySpecificTimes | null,
      })) as ShiftTemplate[];
    },
    enabled: !!patientId,
  });
}

export function useCreateShiftTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateShiftTemplateInput) => {
      const { data, error } = await supabase
        .from('shift_templates')
        .insert({
          patient_id: input.patient_id,
          name: input.name,
          default_start_time: input.default_start_time,
          default_end_time: input.default_end_time,
          day_specific_times: (input.day_specific_times || null) as unknown as Json,
          has_variable_hours: input.has_variable_hours || false,
          is_overnight: input.is_overnight || false,
          auto_rollover: input.auto_rollover ?? false,
          alarm_enabled: input.alarm_enabled ?? false,
          alarm_sound: input.alarm_sound ?? 'bell',
          alarm_vibrate: input.alarm_vibrate ?? true,
          alarm_repeat_minutes: input.alarm_repeat_minutes ?? 5,
          alarm_max_repeats: input.alarm_max_repeats ?? 3,
        } as any)

        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shift-templates', data.patient_id] });
      toast.success('Escala criada com sucesso!');
    },
    onError: (error) => {
      console.error('Error creating shift template:', error);
      toast.error('Erro ao criar escala');
    },
  });
}

export function useUpdateShiftTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, patientId, ...input }: UpdateShiftTemplateInput & { id: string; patientId: string }) => {
      const { data, error } = await supabase
        .from('shift_templates')
        .update({
          name: input.name,
          default_start_time: input.default_start_time,
          default_end_time: input.default_end_time,
          day_specific_times: (input.day_specific_times ?? null) as unknown as Json,
          has_variable_hours: input.has_variable_hours,
          is_overnight: input.is_overnight,
          auto_rollover: input.auto_rollover,
          alarm_enabled: input.alarm_enabled,
          alarm_sound: input.alarm_sound,
          alarm_vibrate: input.alarm_vibrate,
          alarm_repeat_minutes: input.alarm_repeat_minutes,
          alarm_max_repeats: input.alarm_max_repeats,
        } as any)

        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, patientId };
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['shift-templates', data.patientId] });
      toast.success('Escala atualizada!');

      // Sincronizar plantões ativos que usam esta escala
      try {
        const { data: activeShifts, error: shiftError } = await supabase
          .from('shifts')
          .select('*')
          .eq('patient_id', data.patientId)
          .in('status', ['active', 'in_progress']);

        if (shiftError) throw shiftError;

        const affected = (activeShifts || []).filter(
          (s) => s.shift_template_id === data.id || s.original_template_id === data.id,
        );

        if (affected.length === 0) return;

        const { data: session } = await supabase.auth.getSession();
        const currentUserId = session.session?.user.id;
        let totalAdded = 0;
        let totalRemoved = 0;

        for (const activeShift of affected) {
          // 1. Incluir novas atividades dentro do novo horário
          const { data: createdCount, error: rpcError } = await supabase.rpc('instantiate_shift_tasks', {
            p_shift_id: activeShift.id,
            p_patient_id: data.patientId,
            p_check_in_time: activeShift.check_in_at,
            p_shift_template_id: activeShift.shift_template_id ?? data.id,
          });

          if (rpcError) {
            console.error('Error recalculating tasks:', rpcError);
          } else if (createdCount && createdCount > 0) {
            totalAdded += createdCount as number;
          }

          // 2. Remover apenas atividades pendentes fora do novo horário (histórico é preservado)
          const { data: removedCount, error: cleanupError } = await supabase.rpc('cleanup_shift_tasks' as any, {
            p_shift_id: activeShift.id,
            p_shift_template_id: activeShift.shift_template_id ?? data.id,
          });

          if (cleanupError) {
            console.error('Error cleaning up tasks:', cleanupError);
          } else if (removedCount && (removedCount as number) > 0) {
            totalRemoved += removedCount as number;
          }

          // Notificar o cuidador do plantão, se não for o usuário atual
          const changed =
            (createdCount && (createdCount as number) > 0) ||
            (removedCount && (removedCount as number) > 0);

          if (changed && activeShift.caregiver_id !== currentUserId) {
            await supabase.from('notifications').insert({
              user_id: activeShift.caregiver_id,
              patient_id: data.patientId,
              title: 'Atualização de plantão',
              message:
                'O horário da escala mudou: as atividades do seu plantão foram ajustadas. Atividades já registradas foram mantidas.',
              type: 'shift_update',
            });
          }

          queryClient.invalidateQueries({ queryKey: ['shift-task-instances', activeShift.id] });
          queryClient.invalidateQueries({ queryKey: ['task-instances', activeShift.id] });
        }

        queryClient.invalidateQueries({ queryKey: ['shift-tasks', data.patientId] });

        if (totalAdded > 0 && totalRemoved > 0) {
          toast.info(`Plantão ativo atualizado: ${totalAdded} atividade(s) incluída(s) e ${totalRemoved} removida(s).`);
        } else if (totalAdded > 0) {
          toast.info(`Plantão ativo atualizado com ${totalAdded} nova(s) atividade(s).`);
        } else if (totalRemoved > 0) {
          toast.info(`Plantão ativo atualizado: ${totalRemoved} atividade(s) pendente(s) removida(s).`);
        }
      } catch (err) {
        console.error('Error processing active shift update:', err);
      }
    },

    onError: (error) => {
      console.error('Error updating shift template:', error);
      toast.error('Erro ao atualizar escala');
    },
  });
}

export function useDeleteShiftTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, patientId }: { id: string; patientId: string }) => {
      // Primeiro, desvincular cuidadores deste template
      await supabase
        .from('patient_user_links')
        .update({ shift_template_id: null })
        .eq('shift_template_id', id);

      // Soft delete: marcar como deletado
      const { error } = await supabase
        .from('shift_templates')
        .update({ deleted_at: new Date().toISOString() } as any)
        .eq('id', id);

      if (error) throw error;
      return { patientId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shift-templates', data.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient-members', data.patientId] });
      toast.success('Escala excluída!');
    },
    onError: (error) => {
      console.error('Error deleting shift template:', error);
      toast.error('Erro ao excluir escala');
    },
  });
}

// Hook to update caregiver's shift template assignment
export function useAssignShiftTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      linkId,
      shiftTemplateId,
      patientId
    }: {
      linkId: string;
      shiftTemplateId: string | null;
      patientId: string;
    }) => {
      const { error } = await supabase
        .from('patient_user_links')
        .update({ shift_template_id: shiftTemplateId })
        .eq('id', linkId);

      if (error) throw error;
      return { patientId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['caregivers', data.patientId] });
      queryClient.invalidateQueries({ queryKey: ['patient-user-links', data.patientId] });
      toast.success('Turno do cuidador atualizado!');
    },
    onError: (error) => {
      console.error('Error assigning shift template:', error);
      toast.error('Erro ao atribuir turno');
    },
  });
}
